import { stripDiacritics } from './dutchNameNormalizer.ts';
import type { AlternativeName, Person } from '../types.ts';

// Dutch surname prefixes (tussenvoegsels) to skip when extracting initials
const PREFIXES = [
  'van de',
  'van der',
  'van den',
  'van het',
  'van',
  'de',
  'den',
  'der',
  'het',
  "'t",
  'te',
  'ter',
  'ten',
  'bij',
  'op',
  'in',
];

/**
 * Format a full person name including optional surnamePrefix (tussenvoegsel)
 * and optional patronym. Order: [givenNames] [patronym] [surnamePrefix] [surname].
 * Examples:
 *   formatFullName("Jan", "van den", "Berg")               → "Jan van den Berg"
 *   formatFullName("Maria", undefined, "Jansen")           → "Maria Jansen"
 *   formatFullName("Jan", "", "Berg")                      → "Jan Berg"
 *   formatFullName("Jan", undefined, "Kalden", "Pietersz") → "Jan Pietersz Kalden"
 *   formatFullName("Aaltje", "van", "Berg", "Lambertsdr")  → "Aaltje Lambertsdr van Berg"
 */
export function formatFullName(
  givenNames?: string | null,
  surnamePrefix?: string | null,
  surname?: string | null,
  patronym?: string | null
): string {
  return [givenNames, patronym, surnamePrefix, surname]
    .filter(Boolean)
    .join(' ')
    .replace(/\s*;\s*/g, ', ') // GEDCOM semicolons → commas
    .replace(/^[,\s]+/, '') // strip leading commas/spaces
    .replace(/\s+/g, ' ')
    .trim();
}

/** Volledige naam van een persoon (met patroniem en tussenvoegsel); '' zonder persoon. */
export const naamVan = (p?: Partial<Person> | null): string =>
  p ? formatFullName(p.givenNames, p.surnamePrefix, p.surname, p.patronym) : '';

/**
 * Extract two-letter initials: first letter of givenNames + first letter of actual surname
 * Skips Dutch prefixes (tussenvoegsels) like "de", "van", "van de", etc.
 *
 * Examples:
 *   getInitials("Maria", "de Leeuw")  → "ML"
 *   getInitials("Jan", "van der Berg") → "JB"
 *   getInitials("Pieter", "Jansen")    → "PJ"
 */
export function getInitials(givenNames?: string, surname?: string): string {
  const first = givenNames?.charAt(0).toUpperCase() || '?';

  if (!surname) return first;

  const lower = surname.toLowerCase().trim();
  for (const prefix of PREFIXES) {
    if (lower.startsWith(prefix + ' ')) {
      const rest = surname.slice(prefix.length).trim();
      if (rest) return first + rest.charAt(0).toUpperCase();
    }
  }

  return first + surname.charAt(0).toUpperCase();
}

/**
 * Normalize een zoekstring of naam-fragment voor matching:
 * lowercase + diakritieken-strip + multi-spatie collapse + trim.
 * Zo matcht 'Renée Müller' op zoekquery 'Renee Muller' (en omgekeerd).
 */
export function normalizeForSearch(s: string): string {
  return stripDiacritics(s.toLowerCase()).replace(/\s+/g, ' ').trim();
}

/**
 * Match een persoon tegen een vrije-tekst zoekquery. Gebruikt door alle
 * persoon-zoekers (PersonManager filter, CommandPalette, ParticipantPicker,
 * PersonCombobox, SearchPage, SourceManager filter, RelationshipCalculator)
 * om consistent gedrag te garanderen, zie #760.
 *
 * Match-volgorde:
 *  1. Hoofdnaam mét tussenvoegsel  ('Renee de Kamp'.includes('renee de'))
 *  2. Hoofdnaam zónder tussenvoegsel ('Renee Kamp'.includes('renee kamp'))
 *  3. Hoofdnaam met patronym (#781) ('Jan Pietersz Kalden'.includes('jan pietersz'))
 *  4. Patronym alleen ('pietersz'.includes('pietersz'))
 *  5. Direct maiden_name DB-veld
 *  6. Alternatieve namen (incl. type='maiden'), beide prefix-varianten
 *
 * Diakritieken worden in zowel query als naam gestript (NFD), dus
 * 'Müller' is vindbaar via 'Muller' en omgekeerd.
 */
export function personMatchesQuery(
  person: {
    givenNames?: string;
    surnamePrefix?: string;
    surname?: string;
    patronym?: string;
    maidenName?: string;
    alternativeNames?: AlternativeName[];
  },
  query: string
): boolean {
  const q = normalizeForSearch(query);
  if (!q) return true;

  const variants = (g?: string, p?: string, s?: string, pat?: string) => {
    const withPrefix = normalizeForSearch(`${g || ''} ${p || ''} ${s || ''}`);
    const withoutPrefix = normalizeForSearch(`${g || ''} ${s || ''}`);
    const list: string[] = [];
    list.push(withPrefix);
    // Zonder-prefix variant alleen toevoegen als prefix daadwerkelijk verschil maakt
    if (p) list.push(withoutPrefix);
    // Patronym-varianten (#781)
    if (pat) {
      list.push(normalizeForSearch(`${g || ''} ${pat} ${s || ''}`));
      if (p) list.push(normalizeForSearch(`${g || ''} ${pat} ${p} ${s || ''}`));
    }
    return list;
  };

  // Hoofdnaam
  if (
    variants(person.givenNames, person.surnamePrefix, person.surname, person.patronym).some((v) =>
      v.includes(q)
    )
  ) {
    return true;
  }

  // Patronym alleen (#781) — voor zoekquery's als 'Pietersz'
  if (person.patronym && normalizeForSearch(person.patronym).includes(q)) {
    return true;
  }

  // Direct maiden_name veld — accepteer zowel camelCase als snake_case
  // (Person-type uit API levert soms snake_case voor dit veld)
  const maiden = person.maidenName ?? (person as Record<string, unknown>).maiden_name;
  if (typeof maiden === 'string' && maiden && normalizeForSearch(maiden).includes(q)) {
    return true;
  }

  // Alternatieve namen (incl. type='maiden')
  return (person.alternativeNames || []).some((an) =>
    variants(an.givenNames, an.surnamePrefix, an.surname).some((v) => v.includes(q))
  );
}

// ── Export-redesign (#525) ─────────────────────────────────────────────────
// Compact display-helpers voor persoon-kaarten.

/**
 * Roepnaam + initialen voor de overige voornamen.
 *
 *   {givenNames:'Heinrich Gerhard Wilhelm', nickname:'Henk', surnamePrefix:'van', surname:'Uem'}
 *     → "Henk G.W. van Uem"
 *   {givenNames:'Heinrich Gerhard Wilhelm', surnamePrefix:'van', surname:'Uem'}
 *     → "Heinrich G.W. van Uem"
 */
/**
 * Roepnaam + initialen van de overige voornamen, zónder achternaam. Voor plekken
 * die voornaam en achternaam op eigen regels zetten (proband-cirkel).
 *
 *   {givenNames:'Heinrich Gerhard Wilhelm', nickname:'Henk'} → "Henk G.W."
 *   {givenNames:'Heinrich Gerhard Wilhelm'}                  → "Heinrich G.W."
 */
export function formatGivenNamesShort(person: Person): string {
  const given = (person.givenNames || '').trim().split(/\s+/).filter(Boolean);
  if (given.length === 0) return '';
  // De roepnaam vervangt het eerste given-name; resterende voornamen worden initialen.
  const first = person.nickname?.trim() || given[0];
  const restInitials = given
    .slice(1)
    .map((n) => `${n[0].toUpperCase()}.`)
    .join('');
  return [first, restInitials].filter(Boolean).join(' ').trim();
}

export function formatRoepnaamInitials(person: Person): string {
  const given = (person.givenNames || '').trim().split(/\s+/).filter(Boolean);
  if (given.length === 0) {
    return formatFullName(undefined, person.surnamePrefix, person.surname);
  }

  const surnamePart = [person.surnamePrefix, person.surname].filter(Boolean).join(' ');
  return [formatGivenNamesShort(person), surnamePart]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Alle voornamen als initialen (geen roepnaam vervanging).
 *
 *   {givenNames:'Heinrich Gerhard Wilhelm', surnamePrefix:'van', surname:'Uem'}
 *     → "H.G.W. van Uem"
 */
export function formatInitialsName(person: Person): string {
  const given = (person.givenNames || '').trim().split(/\s+/).filter(Boolean);
  const initials = given.map((n) => `${n[0].toUpperCase()}.`).join('');
  const surnamePart = [person.surnamePrefix, person.surname].filter(Boolean).join(' ');
  return [initials, surnamePart].filter(Boolean).join(' ').trim();
}

const PATRONYMIC_SUFFIXES: ReadonlyArray<{ suffix: string; short: string }> = [
  { suffix: 'zoon', short: '.zn' },
  { suffix: 'dochter', short: '.dr' },
  { suffix: 'zn', short: '.zn' },
  { suffix: 'dr', short: '.dr' },
];

/**
 * Kapt lange patroniemen af voor compacte kaarten.
 *
 * Regel: behoud de stam in zijn geheel als die niet langer is dan het
 * oorspronkelijke suffix (anders zou de afkorting niets winnen). Zo niet,
 * truncate de stam tot `maxLength - short.length` chars.
 *
 *   "Hendrikszoon"   → "Hendr.zn"   (stam 8 chars > 'zoon' 4 → afkappen)
 *   "Aaltjesdochter" → "Aaltjes.dr" (stam 7 chars ≤ 'dochter' 7 → behouden)
 *   "Verwoert"       → "Verwoert"   (geen patroniem-suffix → ongewijzigd)
 */
export function truncatePatronymic(name: string, maxLength = 8): string {
  for (const { suffix, short } of PATRONYMIC_SUFFIXES) {
    if (name.endsWith(suffix)) {
      const stem = name.slice(0, -suffix.length);
      // Behoud de stam volledig als die het oorspronkelijke suffix niet overstijgt.
      if (stem.length <= suffix.length) {
        return stem + short;
      }
      // Anders: kap stam af tot maxLength - short.length chars.
      return stem.slice(0, maxLength - short.length) + short;
    }
  }
  return name;
}

/**
 * CSS-modifier voor het avatar-rondje: 'male' | 'female' | 'unknown'. De enige vertaling van
 * geslacht naar avatar-klasse; elk avatar-rondje gebruikt deze (#1837).
 */
export function genderClass(p: { gender?: string | null }): 'male' | 'female' | 'unknown' {
  const g = p.gender;
  if (g === 'M' || g === 'male') return 'male';
  if (g === 'F' || g === 'female') return 'female';
  return 'unknown';
}
